import { MailWarning, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { pagesOpenedBy } from '@obraplan/core/domain/access/pages';
import { PERMISSION, type PermissionKey } from '@obraplan/core/domain/access/permissions';
import type { RoleAssignment } from '@obraplan/core/domain/access/permission-resolver';
import { isLockedRole, type Role } from '@obraplan/core/domain/access/role';
import { ROLE } from '@obraplan/core/domain/access/roles';
import { assignableRoles, holdsSuperAdmin } from '@obraplan/core/domain/access/super-admin';
import type { User } from '@obraplan/core/domain/access/user';
import type { RoleId, UserId } from '@obraplan/core/domain/shared/ids';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { ApiKeysCard } from '@/features/session/ApiKeysCard';
import { AssignRoleDialog, type AssignRoleSubmit } from '@/features/session/AssignRoleDialog';
import { PageSummary } from '@/features/session/PageSummary';
import { RoleEditorDialog, type RoleEditorSubmit } from '@/features/session/RoleEditorDialog';
import { profileAccess } from '@/features/session/profile-access';
import { type ProfileFormValues } from '@/features/session/profile-form';
import { SetPasswordDialog } from '@/features/session/SetPasswordDialog';
import { TransferSuperAdminDialog } from '@/features/session/TransferSuperAdminDialog';
import { UserEditDialog } from '@/features/session/UserEditDialog';
import {
  useContainer,
  usePermission,
  usePermissionGlobally,
  useSession,
} from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório.').max(120),
  // Obrigatório desde a Fase 12: é por ele que a pessoa entra no sistema, e é a chave
  // que o sistema de compras usa para identificar quem requisitou.
  email: z.string().trim().min(1, 'E-mail é obrigatório.').email('E-mail inválido.').max(254),
});
type FormValues = z.infer<typeof formSchema>;

/**
 * Perfis e papéis.
 *
 * Duas tabelas, e a ligação entre elas é o que a tela existe para mostrar: a pessoa tem
 * papéis, e o papel diz que telas ela abre. As duas mostram as telas — na do papel, o que
 * ele libera; na da pessoa, o que ela vai ver somando os papéis que tem.
 *
 * A interface só **esconde** o que o usuário não pode fazer; quem recusa é o caso de uso
 * (RN-134). Por isso o botão de criar some sem permissão, mas a verificação continua
 * acontecendo do outro lado.
 *
 * Quem cuida das pessoas (`users.manage`, o Administrador) vê os perfis e escolhe o papel de
 * cada um, menos o de Super administrador — que ele também não edita, desativa nem tem a
 * senha redefinida por ele. A tabela de papéis, com o que cada um abre, é de quem tem
 * `roles.manage`: o titular, que é quem a transfere (RN-286 a RN-290).
 */
export function ProfilesPage() {
  const container = useContainer();
  const session = useSession();
  const canManageUsers = usePermission(PERMISSION.USERS_MANAGE);
  const canManageRoles = usePermission(PERMISSION.ROLES_MANAGE);
  // Papel é sistema, não obra: editar exige o papel global, como no caso de uso.
  const canEditRoles = usePermissionGlobally(PERMISSION.ROLES_MANAGE);
  // A chave de API lê o sistema inteiro: emitir é global, como no caso de uso.
  const canManageApiKeys = usePermissionGlobally(PERMISSION.API_KEYS_MANAGE);

  const [createOpen, setCreateOpen] = useState(false);
  const [assignFor, setAssignFor] = useState<{ id: UserId; name: string } | undefined>(undefined);
  const [editing, setEditing] = useState<User | undefined>(undefined);
  const [senhaPara, setSenhaPara] = useState<User | undefined>(undefined);
  const [roleEditor, setRoleEditor] = useState<{ role?: Role } | undefined>(undefined);
  const [roleToDelete, setRoleToDelete] = useState<Role | undefined>(undefined);
  const [transferOpen, setTransferOpen] = useState(false);

  // Inclui os inativos: é aqui que eles voltam a ser ativos, e um perfil que sumisse da
  // tela por estar desativado não teria por onde ser reativado.
  const users = useLive(
    () => container.listUsers.execute({ includeInactive: true }, session),
    [session.userId],
  );
  const roles = useLive(() => container.listRoles.execute(), []);
  // As obras que **quem concede** alcança: ninguém dá acesso a uma obra que não vê.
  const projects = useLive(
    () => container.listAccessibleProjects.execute(session),
    [session.userId],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '' },
  });

  const [createUser, createState] = useCommand(
    (values: FormValues) => container.createUser.execute(values, session),
    {
      successMessage: 'Perfil criado.',
      silent: true,
      onSuccess: () => {
        form.reset();
        setCreateOpen(false);
      },
    },
  );

  const [toggleActive] = useCommand(
    (userId: UserId, name: string, isActive: boolean) =>
      container.updateUser.execute({ userId, name, isActive }, session),
    { successMessage: 'Perfil atualizado.' },
  );

  // A situação vai junto porque o contrato a exige, e é a de quem está sendo editado: a
  // edição de cadastro não ativa nem desativa ninguém. E-mail em branco é omitido — no
  // contrato, ausente significa **manter**, e mandar vazio seria apagar a porta de entrada.
  const [updateUser, updateState] = useCommand(
    (user: User, values: ProfileFormValues) =>
      container.updateUser.execute(
        {
          userId: user.id,
          name: values.name,
          isActive: user.isActive,
          ...(values.email === '' ? {} : { email: values.email }),
        },
        session,
      ),
    {
      successMessage: 'Perfil atualizado.',
      silent: true,
      onSuccess: () => setEditing(undefined),
    },
  );

  // Não existe autocadastro nem "esqueci minha senha": a senha nunca volta, só a auditoria
  // registra que mudou.
  const [setPassword, senhaState] = useCommand(
    (userId: UserId, password: string) =>
      container.setPassword.execute({ userId, password }, session),
    {
      successMessage: 'Senha definida.',
      silent: true,
      onSuccess: () => setSenhaPara(undefined),
    },
  );

  const [assignRole, assignState] = useCommand(
    (userId: UserId, input: AssignRoleSubmit) =>
      container.assignRole.execute(
        {
          userId,
          roleId: input.roleId,
          ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
        },
        session,
      ),
    { successMessage: 'Papel atribuído.', silent: true, onSuccess: () => setAssignFor(undefined) },
  );

  // A revogação carrega o **alcance**: o mesmo papel pode existir global e em uma obra,
  // e sem o `projectId` a tela revogaria um pelo outro.
  const [revokeRole] = useCommand(
    (userId: UserId, roleId: RoleId, projectId: string | undefined) =>
      container.revokeRole.execute(
        { userId, roleId, ...(projectId === undefined ? {} : { projectId }) },
        session,
      ),
    { successMessage: 'Papel revogado.' },
  );

  const [transferSuperAdmin, transferState] = useCommand(
    (userId: string) => container.transferSuperAdmin.execute({ userId }, session),
    {
      successMessage: 'Papel de Super administrador transferido.',
      silent: true,
      onSuccess: () => setTransferOpen(false),
    },
  );

  const [createRole, createRoleState] = useCommand(
    (input: RoleEditorSubmit) => container.createRole.execute(input, session),
    { successMessage: 'Papel criado.', silent: true, onSuccess: () => setRoleEditor(undefined) },
  );

  const [updateRole, updateRoleState] = useCommand(
    (roleId: string, input: RoleEditorSubmit) =>
      container.updateRole.execute({ roleId, ...input }, session),
    {
      successMessage: 'Papel atualizado. Quem já está no sistema vê a mudança ao recarregar.',
      silent: true,
      onSuccess: () => setRoleEditor(undefined),
    },
  );

  const [deleteRole, deleteRoleState] = useCommand(
    (roleId: string) => container.deleteRole.execute({ roleId }, session),
    {
      successMessage: 'Papel excluído.',
      silent: true,
      onSuccess: () => setRoleToDelete(undefined),
    },
  );

  const roleById = useMemo(
    () => new Map((roles ?? []).map((role) => [role.id as string, role])),
    [roles],
  );

  // O titular de hoje, e se é quem olha: só ele vê o botão de transferir.
  const superRole = roles?.find((role) => role.key === ROLE.SUPER_ADMIN);
  const isTitular = holdsSuperAdmin(session.assignments, session.roles);
  const titularName =
    superRole === undefined || users === undefined || !users.ok
      ? undefined
      : users.value.find(({ assignments }) =>
          assignments.some(
            (assignment) =>
              assignment.roleId === superRole.id && assignment.projectId === undefined,
          ),
        )?.user.name;

  // O que a tela oferece em cada linha: o Administrador cuida das pessoas, menos do titular.
  const accessOf = (user: User, assignments: readonly RoleAssignment[]) =>
    profileAccess({
      actorId: session.userId,
      targetId: user.id,
      targetAssignments: assignments,
      rolesById: roleById,
      canManageUsers,
      canManageRoles,
    });

  // Quantas pessoas têm cada papel, em qualquer obra — é o que diz o alcance de uma edição
  // antes de ela ser feita, e o que impede excluir um papel em uso.
  const holdersByRole = useMemo(() => {
    const holders = new Map<string, Set<string>>();
    if (users === undefined || !users.ok) return holders;
    for (const { user, assignments } of users.value) {
      for (const assignment of assignments) {
        const set = holders.get(assignment.roleId) ?? new Set<string>();
        set.add(user.id);
        holders.set(assignment.roleId, set);
      }
    }
    return holders;
  }, [users]);

  // Quem não administra perfis nem papéis não tem o que fazer aqui — e a lista de quem
  // opera o sistema, com o alcance de cada um, não é informação de todo mundo (RN-132).
  if (!canManageUsers && !canManageRoles) {
    return (
      <EmptyState
        title="Sem acesso a esta tela"
        description="Exige as permissões de administração."
        icon={<ShieldAlert className="size-8" />}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Perfis</h1>
          <p className="text-(--color-content-muted)">
            Quem pode operar o sistema e o que cada um pode fazer.
          </p>
        </div>
        {canManageUsers && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden="true" />
            Novo perfil
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Perfis cadastrados</CardTitle>
          <CardDescription>Cada papel vale em uma obra ou em todas.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {users === undefined || roles === undefined ? (
            <LoadingState />
          ) : !users.ok ? (
            <ErrorState message={users.error.message} code={users.error.code} />
          ) : users.value.length === 0 ? (
            <EmptyState title="Nenhum perfil cadastrado" />
          ) : (
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Perfil</TableHead>
                    <TableHead>E-mail</TableHead>
                    <TableHead>Papéis</TableHead>
                    <TableHead>Telas que vê</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.value.map(({ user, assignments, roleNames }) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>
                        {user.email === undefined ? (
                          // Perfil sem e-mail não entra no sistema — dito aqui, não só no cadastro.
                          <Badge tone="warn" icon={<MailWarning aria-hidden="true" />}>
                            Sem e-mail — não entra
                          </Badge>
                        ) : (
                          <span className="text-sm text-(--color-content-muted)">{user.email}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {roleNames.length === 0 ? (
                            <span className="text-sm text-(--color-content-muted)">—</span>
                          ) : (
                            assignments.map((assignment) => {
                              const role = roleById.get(assignment.roleId);
                              if (role === undefined) return null;
                              const scope =
                                assignment.projectId === undefined
                                  ? 'todas as obras'
                                  : (projects?.find(
                                      (project) => project.id === assignment.projectId,
                                    )?.name ?? 'obra sem acesso');
                              return (
                                <span
                                  key={`${assignment.roleId}-${assignment.projectId ?? 'global'}`}
                                  className="inline-flex items-center gap-1"
                                >
                                  <Badge
                                    tone={assignment.projectId === undefined ? 'warn' : 'info'}
                                  >
                                    {role.name} · {scope}
                                  </Badge>
                                  {accessOf(user, assignments).canRevoke(role.key) && (
                                    <button
                                      type="button"
                                      aria-label={`Revogar ${role.name} de ${user.name} em ${scope}`}
                                      className="rounded p-0.5 text-(--color-content-muted) hover:text-(--color-state-danger)"
                                      onClick={() =>
                                        void revokeRole(user.id, role.id, assignment.projectId)
                                      }
                                    >
                                      <Trash2 className="size-3.5" aria-hidden="true" />
                                    </button>
                                  )}
                                </span>
                              );
                            })
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-48">
                        <PageSummary pages={pagesOfAssignments(assignments, roleById)} />
                      </TableCell>
                      <TableCell>
                        {user.isActive ? (
                          <Badge tone="ok">Ativo</Badge>
                        ) : (
                          <Badge tone="neutral">Inativo</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-2">
                          {accessOf(user, assignments).isTitular &&
                            !accessOf(user, assignments).canEdit && (
                              <span className="self-center text-sm text-(--color-content-muted)">
                                Só o Super administrador altera este perfil
                              </span>
                            )}
                          {accessOf(user, assignments).canAssign && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setAssignFor({ id: user.id, name: user.name })}
                            >
                              Atribuir papel
                            </Button>
                          )}
                          {accessOf(user, assignments).canEdit && (
                            <Button variant="secondary" size="sm" onClick={() => setEditing(user)}>
                              Editar
                            </Button>
                          )}
                          {accessOf(user, assignments).canSetPassword && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setSenhaPara(user)}
                            >
                              Definir senha
                            </Button>
                          )}
                          {accessOf(user, assignments).canToggleActive && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => void toggleActive(user.id, user.name, !user.isActive)}
                            >
                              {user.isActive ? 'Desativar' : 'Reativar'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>
          )}
        </CardContent>
      </Card>

      {canManageApiKeys && <ApiKeysCard />}

      {canManageRoles && (
        <Card>
          <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <CardTitle>Papéis</CardTitle>
              <CardDescription>
                O papel define as telas que a pessoa vê e o que ela pode fazer em cada uma.
              </CardDescription>
            </div>
            {canEditRoles && (
              <Button variant="secondary" size="sm" onClick={() => setRoleEditor({})}>
                <Plus aria-hidden="true" />
                Novo papel
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {roles === undefined ? (
              <LoadingState />
            ) : (
              <TableWrapper>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Papel</TableHead>
                      <TableHead>Telas que abre</TableHead>
                      <TableHead className="text-right">Pessoas</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((role) => {
                      const holders = holdersByRole.get(role.id)?.size ?? 0;
                      const editable = canEditRoles && !isLockedRole(role);
                      return (
                        <TableRow key={role.id}>
                          <TableCell className="min-w-56">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="font-medium">{role.name}</span>
                              {!role.isSystem ? (
                                <Badge tone="info">criado aqui</Badge>
                              ) : role.customizedAt !== undefined && !isLockedRole(role) ? (
                                <Badge tone="warn">editado</Badge>
                              ) : null}
                            </div>
                            {role.description !== undefined && (
                              <p className="text-sm text-(--color-content-muted)">
                                {role.description}
                              </p>
                            )}
                            {role.key === ROLE.SUPER_ADMIN && titularName !== undefined && (
                              <p className="text-sm">Titular: {titularName}</p>
                            )}
                          </TableCell>
                          <TableCell className="min-w-56">
                            <PageSummary pages={pagesOpenedBy(new Set(role.permissions))} />
                          </TableCell>
                          <TableCell className="tabular text-right">{holders}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setRoleEditor({ role })}
                              >
                                {editable ? 'Editar' : 'Ver'}
                              </Button>
                              {role.key === ROLE.SUPER_ADMIN && isTitular && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setTransferOpen(true)}
                                >
                                  Transferir
                                </Button>
                              )}
                              {canEditRoles && !role.isSystem && (
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={holders > 0}
                                  title={
                                    holders > 0
                                      ? 'Revogue o papel de quem o tem antes de excluí-lo.'
                                      : undefined
                                  }
                                  onClick={() => setRoleToDelete(role)}
                                >
                                  Excluir
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableWrapper>
            )}
          </CardContent>
        </Card>
      )}

      <TransferSuperAdminDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        candidates={(users !== undefined && users.ok ? users.value : [])
          .filter(({ user }) => user.isActive && user.id !== session.userId)
          .map(({ user }) => ({
            id: user.id,
            name: user.name,
            ...(user.email === undefined ? {} : { email: user.email }),
          }))}
        isPending={transferState.isPending}
        {...(transferState.error === undefined
          ? {}
          : { errorMessage: transferState.error.message })}
        onSubmit={(userId) => void transferSuperAdmin(userId)}
      />

      <RoleEditorDialog
        open={roleEditor !== undefined}
        onOpenChange={(open) => {
          if (!open) setRoleEditor(undefined);
        }}
        role={roleEditor?.role}
        readOnly={!canEditRoles}
        isPending={createRoleState.isPending || updateRoleState.isPending}
        error={roleEditor?.role === undefined ? createRoleState.error : updateRoleState.error}
        onSubmit={(input) => {
          const role = roleEditor?.role;
          if (role === undefined) void createRole(input);
          else void updateRole(role.id, input);
        }}
      />

      <Dialog
        open={roleToDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) setRoleToDelete(undefined);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir o papel {roleToDelete?.name}?</DialogTitle>
            <DialogDescription>
              Ninguém tem este papel hoje, e a exclusão fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          {deleteRoleState.error !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {deleteRoleState.error.message}
            </p>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRoleToDelete(undefined)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={deleteRoleState.isPending}
              onClick={() => {
                if (roleToDelete !== undefined) void deleteRole(roleToDelete.id);
              }}
            >
              {deleteRoleState.isPending ? 'Excluindo…' : 'Excluir papel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserEditDialog
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        user={editing}
        isPending={updateState.isPending}
        error={updateState.error}
        onSubmit={(values) => {
          if (editing !== undefined) void updateUser(editing, values);
        }}
      />

      <SetPasswordDialog
        open={senhaPara !== undefined}
        onOpenChange={(open) => {
          if (!open) setSenhaPara(undefined);
        }}
        userName={senhaPara?.name ?? ''}
        isPending={senhaState.isPending}
        error={senhaState.error}
        onSubmit={(password) => {
          if (senhaPara !== undefined) void setPassword(senhaPara.id, password);
        }}
      />

      <AssignRoleDialog
        open={assignFor !== undefined}
        onOpenChange={(open) => {
          if (!open) setAssignFor(undefined);
        }}
        userName={assignFor?.name ?? ''}
        roles={assignableRoles(roles ?? [])}
        projects={projects ?? []}
        isPending={assignState.isPending}
        {...(assignState.error === undefined ? {} : { errorMessage: assignState.error.message })}
        onSubmit={(input) => {
          if (assignFor !== undefined) void assignRole(assignFor.id, input);
        }}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo perfil</DialogTitle>
            <DialogDescription>
              O nome identifica quem operou o sistema no histórico de alterações.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit((values) => createUser(values))(event);
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Nome"
              htmlFor="profile-name"
              required
              error={
                form.formState.errors.name?.message ??
                createState.error?.fieldErrors.find((f) => f.field === 'name')?.message ??
                (createState.error?.kind === 'conflict' ? createState.error.message : undefined)
              }
            >
              <Input
                id="profile-name"
                autoFocus
                aria-invalid={form.formState.errors.name !== undefined}
                {...form.register('name')}
              />
            </Field>
            <Field
              label="E-mail"
              htmlFor="profile-email"
              required
              hint="É por ele que a pessoa entra no sistema."
              error={
                form.formState.errors.email?.message ??
                createState.error?.fieldErrors.find((f) => f.field === 'email')?.message
              }
            >
              <Input
                id="profile-email"
                type="email"
                aria-invalid={form.formState.errors.email !== undefined}
                {...form.register('email')}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={createState.isPending}>
                {createState.isPending ? 'Criando…' : 'Criar perfil'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * As telas que a pessoa vê: a soma das telas de todos os papéis dela, em qualquer obra.
 *
 * É a mesma conta do menu (`permissionsAnywhere`) — a coluna mostra o que a pessoa vai
 * encontrar ao entrar, e não o que ela alcança na obra aberta nesta tela.
 */
function pagesOfAssignments(
  assignments: readonly { roleId: string }[],
  roleById: ReadonlyMap<string, Role>,
) {
  const permissions = new Set<PermissionKey>();
  for (const assignment of assignments) {
    for (const key of roleById.get(assignment.roleId)?.permissions ?? []) permissions.add(key);
  }
  return pagesOpenedBy(permissions);
}
